//src/hooks/useUnits.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUnit,
  getUnitDetail,
  listUnits,
  setUnitActive,
  updateUnit,
  type ListUnitsParams,
  type UnitUpsertIn,
} from "@/services/units";
import { useSelectedClientCompany } from "@/hooks/useSelectedClientCompany";

export const UNITS_QK = (clientCompanyId: string | null, params: ListUnitsParams) =>
  ["units", clientCompanyId, params] as const;

export function useUnits(params: ListUnitsParams) {
  const { selectedClientCompanyId } = useSelectedClientCompany();

  return useQuery({
    queryKey: UNITS_QK(selectedClientCompanyId, params),
    queryFn: () => listUnits(params),
    enabled: !!selectedClientCompanyId,
    staleTime: 20_000,
  });
}

export function useUnitDetail(unitId: string | undefined) {
  const { selectedClientCompanyId } = useSelectedClientCompany();

  return useQuery({
    queryKey: ["unit_detail", selectedClientCompanyId, unitId],
    queryFn: () => getUnitDetail(unitId as string),
    enabled: !!unitId && !!selectedClientCompanyId,
    staleTime: 20_000,
  });
}

export function useCreateUnit() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: UnitUpsertIn) => createUnit(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["units"] });
    },
  });
}

export function useUpdateUnit() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UnitUpsertIn }) =>
      updateUnit(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["units"] });
      qc.invalidateQueries({ queryKey: ["unit_detail", variables.id] });
    },
  });
}

export function useSetUnitActive() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setUnitActive(id, is_active),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["units"] });
      qc.invalidateQueries({ queryKey: ["unit_detail", variables.id] });
    },
  });
}
